export interface IResearchStat {
  value: string;
  label: string;
}

export interface IProblemStat {
  value: string;
  label: string;
  source: string;
}

export interface ILogicModelItem {
  text: string;
  cite?: string;
  timeframe?: string;
}

export interface ILogicModelColumn {
  header: string;
  color: string;
  items: ILogicModelItem[];
}

export interface IResearchCitation {
  id: number;
  authors: string;
  year: number;
  title: string;
  journal: string;
  sample: string;
  finding: string;
  designDecision: string;
  doi: string;
}

export interface ISatisfactionEntry {
  label: string;
  question: string;
  positive: number;
  negative: number;
  detail: string;
}

export interface INPSData {
  value: number;
  segments: { range: [number, number]; label: string; color: string }[];
  breakdown: { label: string; pct: string }[];
}

export interface ITestimonialQuote {
  text: string;
  role: string;
}

export const RESEARCH_STATS: IResearchStat[] = [
  { value: '+75', label: 'Net Promoter Score' },
  { value: '76.9%', label: 'Report Reduced Documentation Time' },
  { value: '50%', label: 'More Likely to Stay in Special Education' },
  { value: '78.6%', label: 'Report Better Instructional Decisions' },
];

export const PROBLEM_STATS: IProblemStat[] = [
  {
    value: '>50%',
    label:
      'of special ed teachers experience moderate-to-high emotional exhaustion',
    source: 'Alexaki et al., 2025',
  },
  {
    value: '27%',
    label: 'of variance in intention to quit is explained by burnout alone',
    source: 'Madigan & Kim, 2021',
  },
  {
    value: '#1',
    label:
      'Paperwork burden is the top modifiable factor driving sped teacher attrition',
    source: 'Billingsley & Bettini, 2019',
  },
];

export const LOGIC_MODEL_COLUMNS: ILogicModelColumn[] = [
  {
    header: 'INPUTS',
    color: '#E8F0F8',
    items: [
      { text: 'Ablespace Platform (HIPAA-compliant)' },
      { text: 'Existing district hardware' },
      { text: 'Onboarding & training' },
      { text: 'Imported IEP goals & rosters' },
    ],
  },
  {
    header: 'ACTIVITIES',
    color: '#F0F5FA',
    items: [
      {
        text: 'Digital data entry at point of service',
        cite: 'Ruble et al., 2018',
      },
      {
        text: 'Automated billing & documentation',
        cite: 'Alexaki et al., 2025',
      },
      {
        text: 'Auto-generated graphs & trend lines',
        cite: 'van den Bosch et al., 2019',
      },
      {
        text: 'One-click family progress reports',
        cite: 'Bergman & Chan, 2021',
      },
    ],
  },
  {
    header: 'OUTPUTS',
    color: '#E8F5E9',
    items: [
      { text: 'Increased data points per student' },
      { text: 'Reduced non-instructional hours' },
      { text: '100% of goals with progress graphs' },
      { text: 'More frequent family updates' },
    ],
  },
  {
    header: 'OUTCOMES',
    color: '#FFF8E1',
    items: [
      {
        text: 'More consistent progress monitoring',
        timeframe: '0\u20136 months',
      },
      {
        text: 'Reduced teacher burnout; stronger family engagement',
        timeframe: '6\u201312 months',
      },
      {
        text: 'Improved retention; better student goal mastery',
        timeframe: '1+ year',
      },
    ],
  },
];

export const RESEARCH_CITATIONS: IResearchCitation[] = [
  {
    id: 1,
    authors: 'Alexaki, P., Antonopoulou, H., Gkintoni, E., et al.',
    year: 2025,
    title:
      'Psychological dimensions of professional burnout in special education',
    journal:
      'International Journal of Environmental Research and Public Health',
    sample: 'N = 114 special education teachers',
    finding:
      'Emotional exhaustion emerged as the central burnout dimension and primary intervention target among special education teachers, with over half experiencing moderate-to-high exhaustion levels. Employment status was the strongest predictor of burnout risk.',
    designDecision: 'Automated Billing & Workflows',
    doi: '10.3390/ijerph22091420',
  },
  {
    id: 2,
    authors: 'Madigan, D. J., & Kim, L. E.',
    year: 2021,
    title: 'Towards an understanding of teacher attrition',
    journal: 'Teaching and Teacher Education',
    sample: 'N = 6,678 teachers (meta-analysis)',
    finding:
      'Burnout and job satisfaction together explain 27% of variance in teachers\u2019 intentions to quit. Emotional exhaustion shows the strongest relationship with quitting intentions.',
    designDecision: 'Workflow Efficiency Features',
    doi: '10.1016/j.tate.2021.103425',
  },
  {
    id: 3,
    authors: 'van den Bosch, R. M., Espin, C. A., Pat-El, R. J., & Saab, N.',
    year: 2019,
    title:
      'Improving teachers\u2019 comprehension of CBM progress-monitoring graphs',
    journal: 'Journal of Learning Disabilities',
    sample: 'N = 164 teachers (RCT)',
    finding:
      'Teachers showed significant deficits in interpreting progress monitoring graphs without structured visual supports and explicit training.',
    designDecision: '20+ Auto-Built Graph Templates',
    doi: '10.1177/0022219419856013',
  },
  {
    id: 4,
    authors:
      'Jung, P. G., McMaster, K. L., Kunkel, A. K., Shin, J., & Stecker, P. M.',
    year: 2018,
    title:
      'Effects of data-based individualization for students with intensive learning needs',
    journal: 'Learning Disabilities Research & Practice',
    sample: 'k = 14 studies, 57 effect sizes (meta-analysis)',
    finding:
      'Data-based individualization produced positive effects on student outcomes across academic areas, with effect sizes of g = 0.37 (DBI alone) and g = 0.38 (DBI with decision supports) \u2014 demonstrating that systematic progress monitoring with decision rules improves outcomes for students with intensive needs.',
    designDecision: 'Mobile Data Entry with Decision Support',
    doi: '10.1111/ldrp.12172',
  },
  {
    id: 5,
    authors: 'Bergman, P., & Chan, E. W.',
    year: 2021,
    title: 'Leveraging parents through low-cost technology',
    journal: 'Journal of Human Resources',
    sample: '1,137 families across 22 schools (RCT)',
    finding:
      'Automated parent communication reduced course failures by 27% and increased attendance by 12%, with larger effects for lower-performing students.',
    designDecision: 'One-Click Parent Reports',
    doi: '10.3368/jhr.56.1.1118-9837R1',
  },
  {
    id: 6,
    authors: 'Billingsley, B., & Bettini, E.',
    year: 2019,
    title: 'Special education teacher attrition and retention',
    journal: 'Review of Educational Research',
    sample: 'k = 30 studies (systematic review)',
    finding:
      'Administrative burden, inadequate support, and excessive paperwork are consistently the top modifiable factors driving special education teacher attrition.',
    designDecision: 'Comprehensive Workload Reduction',
    doi: '10.3102/0034654319862495',
  },
];

export const SATISFACTION_DATA: ISatisfactionEntry[] = [
  {
    label: 'Meets IEP data collection & monitoring needs',
    question:
      '"Ablespace meets my needs for IEP data collection and progress monitoring."',
    positive: 100.0,
    negative: 0.0,
    detail: '68.8% Strongly Agree, 31.3% Agree',
  },
  {
    label: 'Satisfied with Ablespace overall',
    question: '"Overall, how satisfied are you with Ablespace?"',
    positive: 93.8,
    negative: 0.0,
    detail: '75.0% Very Satisfied, 18.8% Satisfied',
  },
  {
    label: 'Graphs help understand progress & act on it',
    question:
      '"Ablespace graphs and visualizations help me understand student progress and act on it."',
    positive: 87.5,
    negative: 0.0,
    detail: '25.0% Strongly Agree, 50.0% Agree',
  },
  {
    label: 'Helped make better instructional decisions',
    question:
      '"Using Ablespace has helped me make better instructional or service-delivery decisions for my students."',
    positive: 78.6,
    negative: 7.1,
    detail: '42.9% Strongly Agree, 35.7% Agree',
  },
  {
    label: 'Reduced time spent on documentation',
    question:
      '"Since using Ablespace, the time I spend on documentation has..."',
    positive: 76.9,
    negative: 7.7,
    detail: '30.8% Decreased Significantly, 46.2% Decreased Somewhat',
  },
  {
    label: 'Student progress toward IEP goals improved',
    question:
      '"I believe my students\u2019 progress toward IEP goals has improved since I started using Ablespace."',
    positive: 68.8,
    negative: 12.5,
    detail: '43.8% Strongly Agree, 25.0% Agree',
  },
  {
    label: 'More likely to stay in special education',
    question:
      '"Having tools like Ablespace affects my likelihood of staying in special education."',
    positive: 50.0,
    negative: 0.0,
    detail: '37.5% Much More Likely, 12.5% Somewhat More Likely',
  },
];

export const NPS_DATA: INPSData = {
  value: 75,
  segments: [
    { range: [-100, 0], label: 'Needs Improvement', color: '#E53935' },
    { range: [0, 30], label: 'Good', color: '#FF9800' },
    { range: [30, 70], label: 'Great', color: '#8BC34A' },
    { range: [70, 100], label: 'Excellent', color: '#4CAF50' },
  ],
  breakdown: [
    { label: 'Promoters', pct: '81.3%' },
    { label: 'Passives', pct: '12.5%' },
    { label: 'Detractors', pct: '6.3%' },
  ],
};

export const TESTIMONIAL_QUOTES: ITestimonialQuote[] = [
  {
    text: 'I really like being able to have all of my students in one system and see their objectives on one screen to enter data. Data entry is more timely compared to my last format which was using a variety of Google docs.',
    role: 'Special Education Teacher',
  },
  {
    text: 'AbleSpace graphs have allowed me to pinpoint much more quickly when a student is struggling with regression after a school break, and makes it easier for me to provide data when recommending Extended School Year services.',
    role: 'Special Education Teacher',
  },
  {
    text: 'Showing parents graphs and trend lines to show progress has helped our teachers realize that they need to change the way they write goals.',
    role: 'Special Ed Administrator',
  },
  {
    text: 'My BCBAs are pivoting in their intervention based on the data provided.',
    role: 'Special Ed Administrator',
  },
  {
    text: 'Data collection and the AI summarize session to quickly get log notes done and entered in Medical Assistance.',
    role: 'Speech-Language Pathologist',
  },
  {
    text: 'ESY determinations have been much easier to make and communicate with families.',
    role: 'Special Ed Administrator',
  },
];
